import { NgClass } from '@angular/common'
import { Component, DestroyRef, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'

import { CombinedGraphQLErrors, LinkError } from '@apollo/client/errors'
import { TranslatePipe } from '@ngx-translate/core'
import type { Observable } from 'rxjs'
import { EMPTY, catchError, finalize } from 'rxjs'

@Component({
  selector: 'dhp-auth-base-form',
  templateUrl: './auth-base-form.component.html',
  styleUrl: './auth-base-form.component.scss',
  imports: [NgClass, ReactiveFormsModule, TranslatePipe],
})
export class AuthBaseFormComponent {
  private readonly destroyRef = inject(DestroyRef)

  title = input.required<string>()
  subtitle = input<string>()
  isLoading = signal<boolean>(false)
  submitAction = input<(formGroup: FormGroup) => Observable<unknown>>()
  formGroup = input<FormGroup>(new FormGroup({}))
  submitText = input<string>()
  layoutMode = input<'auth' | 'fullWidth'>('auth')
  showHeader = input(true)
  serverError = signal<{ key: string; params?: Record<string, string> } | null>(
    null
  )

  onSubmitClick(): void {
    this.isLoading.set(true)
    this.serverError.set(null)
    const submitObservable = this.submitAction()

    if (submitObservable) {
      submitObservable(this.formGroup())
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error: unknown) => {
            if (CombinedGraphQLErrors.is(error)) {
              error.errors.forEach(({ message, extensions }) => {
                console.error(`GraphQL Error: ${message}`, extensions)
              })
              // GraphQL validation, syntax or resolver errors
              // These error should be handled in the specific context, this is just the fallback
              this.serverError.set({
                key: 'pages.auth.errors.unknownError',
              })
              return EMPTY
            }

            if (LinkError.is(error)) {
              console.error(error)
              this.serverError.set({
                key: 'pages.auth.errors.connectionError',
              })
              return EMPTY
            }

            const typedError = error as Error
            this.serverError.set({
              key: 'pages.auth.errors.genericError',
              params: { name: typedError.name, message: typedError.message },
            })
            return EMPTY
          }),
          finalize(() => {
            this.isLoading.set(false)
          })
        )
        .subscribe()
    }
  }
}
